import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { catalogApi, type ClientInput } from '@/api/catalogApi'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ListState, StatusPill, SuperAdminGate, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { ClientForm } from '@/components/admin/catalog/ClientForm'
import { useCatalogClients, useCatalogMutation } from '@/hooks/useCatalog'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

/** /admin/clients — clients (Customer) with tenant counts (superadmin). */
export default function CatalogClientsPage() {
  return (
    <SuperAdminGate>
      <ClientsList />
    </SuperAdminGate>
  )
}

function ClientsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const debounced = useDebouncedValue(q.trim(), 250)
  const clients = useCatalogClients(debounced)
  const [creating, setCreating] = useState(false)
  const create = useCatalogMutation((input: ClientInput) => catalogApi.createClient(input))

  return (
    <Page>
      <PageHeading
        eyebrow={t('catalog.eyebrow')}
        title={t('catalog.clients.title')}
        actions={
          <>
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
              <Input aria-label={t('catalog.clients.search')} placeholder={t('catalog.clients.search')} className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button onClick={() => setCreating(true)}>{t('catalog.clients.new')}</Button>
          </>
        }
      />
      <ListState query={clients}>
        {(clients.data ?? []).length === 0 ? (
          <p className="py-6 text-meta text-ink-muted">{t('catalog.clients.empty')}</p>
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={thClass}>{t('catalog.clients.cols.name')}</th>
                <th className={`${thClass} w-52`}>{t('catalog.clients.cols.slug')}</th>
                <th className={`${thClass} w-44`}>{t('catalog.clients.cols.timeZone')}</th>
                <th className={`${thClass} w-24`}>{t('catalog.clients.cols.tenants')}</th>
                <th className={`${thClass} w-32`}>{t('catalog.clients.cols.environments')}</th>
                <th className={`${thClass} w-28`}>{t('catalog.clients.cols.status')}</th>
              </tr>
            </thead>
            <tbody>
              {(clients.data ?? []).map((c) => (
                <tr key={c.id} className="border-b border-line hover:bg-surface-subtle">
                  <td className={`${tdClass} font-medium text-ink-strong`}>
                    <Link to={`/admin/clients/${c.id}`} className="hover:underline">
                      {c.name}
                    </Link>
                    {c.emailDomains.length ? <p className="truncate font-mono text-label font-normal text-ink-muted">{c.emailDomains.join(', ')}</p> : null}
                  </td>
                  <td className={`${tdClass} font-mono`}>{c.slug}</td>
                  <td className={tdClass}>{c.timeZone}</td>
                  <td className={`${tdClass} font-mono`}>{c._count.tenants}</td>
                  <td className={`${tdClass} font-mono`}>{c._count.environments}</td>
                  <td className={tdClass}>
                    <StatusPill status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListState>

      <Dialog
        open={creating}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false)
            create.reset()
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('catalog.clients.createTitle')}</DialogTitle>
          </DialogHeader>
          {creating ? (
            <ClientForm
              pending={create.isPending}
              error={create.error}
              onCancel={() => setCreating(false)}
              onSubmit={(input) => create.mutate(input, { onSuccess: (c) => navigate(`/admin/clients/${c.id}?tab=tenants`) })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Page>
  )
}
